import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { BUILDING_GROUPS, BUILDING_MAP } from '../../constants/buildings';
import { formatNumber } from '../../utils/format';
import { GlanceCard } from '../shell/GlanceCard';
import { PercentRow } from '../shell/PercentRow';
import { ConfirmModal } from '../ConfirmModal';
import { useToast } from '../../hooks/useToast';
import { pctForAcres, acresForPct, resolveRazeQty, computeRazeSurplus } from './buildingsPercent';
import { computeRazeCostPerAcre } from './razeCost';
import { computeAutoRaze } from '../../utils/buildAutoPopulate';
import { api } from '../../api/client';
import type { Province } from '../../types/province';
import type { RazeRequest, RazeResponse } from '../../types/raze';
import type { ActiveStrategyDto } from '../../types/strategy';

/** Matches MobileBuildingsShell.tsx/MobileSciencesShell.tsx/MobileTrainingShell.tsx's existing
 *  reserve for MobileTabBar's footprint (SF-132 Decision 4) — no shared named constant exists yet
 *  for this literal (Deferred Item 5); each self-contained mobile shell declares its own copy. */
const TAB_BAR_OFFSET = '96px';

export interface MobileRazePanelProps {
  province: Province;
  activeStrategy: ActiveStrategyDto | null;
  strategyLoading: boolean;
  onRazeSuccess: () => void;
  worldId: string;
  provinceId: string;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * SF-165: mobile-first replacement for `RazePanel.tsx` on the Raze tab — self-contained state,
 * mirroring `RazePanel.tsx`'s shape/props, not lifted to `Buildings.tsx` (Raze has no cross-row
 * barren-budget pool the way Build does, and its own fixed bottom bar is owned locally, matching
 * `MobileSciencesShell.tsx`/`MobileTrainingShell.tsx`'s precedent). Every signal Build's mobile
 * stepper uses is inverted: the stepper reads "raze down to this % of land," a queued change is
 * shown in red, the strategy chip snaps down to a target you're above (not up to one you haven't
 * reached), and the primary action stays destructive-red.
 */
export function MobileRazePanel({
  province,
  activeStrategy,
  strategyLoading,
  onRazeSuccess,
  worldId,
  provinceId,
}: MobileRazePanelProps) {
  const { addToast } = useToast();

  const [razeQty, setRazeQty] = useState<Record<string, number>>({});
  // SF-165 (mirrors SF-143's expanded-row-state shape): collapses the old expandedId/stepperPct
  // scalar pair into one union — two rows editing simultaneously is structurally unrepresentable.
  const [stepper, setStepper] = useState<{ id: string; acres: number } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const totalLand = province.territory.totals.acres;
  const barren = province.territory.totals.barren;
  const availableGold = province.resources.gold;

  const razeCostPerAcre = useMemo(() => computeRazeCostPerAcre(province), [province]);

  // Threaded purely for PercentRow's inProgressPct pill-ratio/sub-label parity with the same
  // building's Build-tab row — in-progress acres never affect the raze ceiling or
  // computeRazeSurplus, both stay owned-only.
  const inProgress = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of province.territory.construction) {
      map[c.building] = (map[c.building] ?? 0) + c.acres;
    }
    return map;
  }, [province.territory.construction]);

  const totalOwned = useMemo(
    () => Object.values(province.territory.buildings).reduce((sum, count) => sum + (count || 0), 0),
    [province.territory.buildings]
  );

  const totalToRaze = useMemo(
    () => Object.values(razeQty).reduce((sum, q) => sum + q, 0),
    [razeQty]
  );
  const totalRazeCost = totalToRaze * razeCostPerAcre;

  // One "point" step, expressed in acres — same convention as Build's stepper.
  const onePctAcres = Math.max(1, acresForPct(totalLand, 1));
  const twoPctAcres = Math.max(1, acresForPct(totalLand, 2));
  const stepperDisplayPct = stepper ? Math.round(pctForAcres(totalLand, stepper.acres)) : 0;

  function handleToggleRow(buildingId: string) {
    if (stepper?.id === buildingId) {
      setStepper(null);
      return;
    }
    const owned = province.territory.buildings[buildingId] ?? 0;
    const queued = razeQty[buildingId] ?? 0;
    // Seed formula: target *remaining* acres implied by whatever raze is already queued for this
    // row (0 if nothing queued, so it seeds to owned) — never round-tripped through percent.
    setStepper({ id: buildingId, acres: owned - queued });
  }

  function writeRazeQty(buildingId: string, targetAcres: number) {
    const owned = province.territory.buildings[buildingId] ?? 0;
    const newQty = resolveRazeQty({ targetAcres, owned });
    setRazeQty((prev) => {
      if (newQty <= 0) {
        const rest = { ...prev };
        delete rest[buildingId];
        return rest;
      }
      return { ...prev, [buildingId]: newQty };
    });
  }

  function adjustStepper(delta: number) {
    if (!stepper) return;
    const owned = province.territory.buildings[stepper.id] ?? 0;
    const nextAcres = clamp(stepper.acres + delta, 0, owned);
    setStepper({ id: stepper.id, acres: nextAcres });
    writeRazeQty(stepper.id, nextAcres);
  }

  function setStepperAcresAbsolute(buildingId: string, acres: number) {
    setStepper({ id: buildingId, acres });
    writeRazeQty(buildingId, acres);
  }

  // SF-179 Component 3: mobile's top-row bulk conformance action — reuses desktop
  // `RazePanel.tsx`'s `handleRazeOptimize` delta computation (`computeAutoRaze`) verbatim, so
  // desktop and mobile can never silently disagree on the same strategy for the same inputs.
  // Resets any in-progress per-row stepper edit the same way Clear already does, so a mid-edit row
  // doesn't retain a stale local value after the bulk write (qa-engineer G).
  function handleRazeToStrategy() {
    if (!activeStrategy?.settings) return;
    const quantities = computeAutoRaze(activeStrategy.settings.buildings, totalLand, province.territory.buildings as Record<string, number>);
    setRazeQty(quantities);
    setStepper(null);
  }

  const handleSubmitClick = () => {
    if (totalToRaze === 0) return;
    // Gold is a soft, submit-time-only constraint (ported unchanged from RazePanel.tsx) — the
    // "Insufficient gold" hint is already persistently visible in the bottom bar whenever this is
    // true, so submit is simply blocked here rather than surfacing a second message.
    if (totalRazeCost > availableGold) return;
    if (totalToRaze > 10) {
      setShowConfirmModal(true);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);

    try {
      const buildings: Record<string, number> = {};
      Object.entries(razeQty).forEach(([id, qty]) => {
        if (qty > 0) buildings[id] = qty;
      });

      const request: RazeRequest = { buildings };
      const response = await api.post<RazeResponse>(
        `/api/worlds/${worldId}/provinces/${provinceId}/raze`,
        request
      );

      if (response.error) {
        addToast(response.error, 'failure');
      } else if (response.data) {
        const result = response.data.result;
        addToast(
          `Razed ${result.totalAcresRazed} acres for ${formatNumber(result.totalCost)} gc. ${formatNumber(result.remainingGold)} gc remaining.`,
          'success'
        );
        setRazeQty({});
        // Phase 3 Decision 5: collapse the stepper alongside razeQty on submit-success — Raze's
        // only bulk mutation, so no bulkMutationVersion-style external signal is needed.
        setStepper(null);
        onRazeSuccess();
      }
    } catch {
      addToast('Failed to submit raze request. Please try again.', 'failure');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    if (totalToRaze === 0) return;
    setRazeQty({});
    setStepper(null);
  };

  const razeBuildingList = Object.entries(razeQty)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => {
      const building = BUILDING_MAP[id];
      return building ? `${qty} ${building.name}` : '';
    })
    .filter(Boolean);

  // Row filtering: owned === 0 buildings are omitted entirely (not rendered disabled) — a Raze
  // row with nothing owned has nothing to act on. Whole group (including its label) is omitted
  // too when every building in it is zero.
  const visibleGroups = BUILDING_GROUPS.map((group) => ({
    ...group,
    buildings: group.buildings.filter((b) => (province.territory.buildings[b.id] ?? 0) > 0),
  })).filter((group) => group.buildings.length > 0);

  // Land summary card values — keep/razing/barren three-segment bar (bespoke, not
  // LandDistributionBar — see Phase 2 Critical Issue #8 / Phase 3 Decision 8).
  const keepAcres = Math.max(0, totalOwned - totalToRaze);
  const keepPct = pctForAcres(totalLand, keepAcres);
  const razingPct = pctForAcres(totalLand, totalToRaze);
  const barrenPct = pctForAcres(totalLand, barren);
  const distAriaLabel = `Land distribution: ${keepPct.toFixed(1)}% keeping, ${razingPct.toFixed(1)}% razing, ${barrenPct.toFixed(1)}% barren`;

  const razePctRounded = Math.round(pctForAcres(totalLand, totalToRaze));
  const bottomBarVisible = totalToRaze > 0;
  // SF-182 Component 3: the "Insufficient gold" warning line adds ~19-20px to the bottom bar's
  // rendered height that the flat 110px reserve doesn't budget for — bump only when it renders.
  const bottomBarPaddingBottom = bottomBarVisible
    ? (totalRazeCost > availableGold ? '130px' : '110px')
    : undefined;

  return (
    <div style={bottomBarPaddingBottom ? { paddingBottom: bottomBarPaddingBottom } : undefined}>
      <GlanceCard title="Raze">
        <div className="flex items-baseline gap-2.5">
          <span className="font-mono text-3xl font-bold" style={{ color: COLORS.text.primary }}>
            {formatNumber(totalOwned)}
          </span>
          <span className="text-xs" style={{ color: COLORS.text.tertiary }}>acres built</span>
          <span className="ml-auto text-right font-mono text-sm" style={{ color: COLORS.text.secondary }}>
            {formatNumber(razeCostPerAcre)} gc/acre
            <small className="block text-[10px] font-sans" style={{ color: COLORS.text.tertiary }}>
              {formatNumber(availableGold)} gold on hand
            </small>
          </span>
        </div>
        <div
          role="img"
          aria-label={distAriaLabel}
          className="h-2 rounded overflow-hidden flex"
          style={{ backgroundColor: COLORS.bg }}
        >
          {keepPct > 0 && <div style={{ width: `${keepPct}%`, backgroundColor: COLORS.text.secondary }} />}
          {razingPct > 0 && <div style={{ width: `${razingPct}%`, backgroundColor: COLORS.negative, opacity: 0.55 }} />}
          {barrenPct > 0 && <div style={{ width: `${barrenPct}%`, backgroundColor: COLORS.bg, borderRight: `1px solid ${COLORS.border}` }} />}
        </div>
        <div className="flex gap-3.5 text-[10px]" style={{ color: COLORS.text.tertiary }}>
          <span>Keeping {keepPct.toFixed(1)}%</span>
          <span>Razing {razingPct.toFixed(1)}%</span>
          <span>Barren {barrenPct.toFixed(1)}%</span>
        </div>
      </GlanceCard>

      {/* SF-179 Component 3: always-visible top quick-actions row — mirrors
          MobileBuildingsShell.tsx's "Quick actions row" exactly. Unlike the fixed bottom bar
          (gated on totalToRaze > 0), this row is reachable from the zero-acres-queued state it
          exists to help with. */}
      <div className="flex items-center gap-2 mt-3">
        <button
          type="button"
          onClick={handleRazeToStrategy}
          aria-disabled={!activeStrategy?.settings}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 rounded-lg"
          style={{
            minHeight: 44,
            backgroundColor: activeStrategy?.settings ? 'rgba(255,85,85,0.08)' : 'transparent',
            border: `1px solid ${activeStrategy?.settings ? 'rgba(255,85,85,0.4)' : COLORS.border}`,
            color: activeStrategy?.settings ? COLORS.negative : COLORS.text.tertiary,
            cursor: activeStrategy?.settings ? 'pointer' : 'default',
          }}
        >
          ⚡ Raze to Strategy
        </button>
      </div>

      <div className="text-[10px] mt-3" style={{ color: COLORS.text.tertiary }}>
        % of your total land ({formatNumber(totalLand)} acres) — nothing-owned buildings hidden
      </div>

      {!activeStrategy?.settings && (
        <div className="text-xs mt-2" style={{ color: COLORS.text.tertiary }}>
          No active strategy —{' '}
          <Link to="/strategy" style={{ color: COLORS.link }}>
            set one to see target guidance
          </Link>
          .
        </div>
      )}

      <div className="flex flex-col gap-3 mt-3">
        {visibleGroups.length === 0 ? (
          <p className="px-1 py-4 text-sm" style={{ color: COLORS.text.tertiary }}>
            No buildings to raze.
          </p>
        ) : (
          visibleGroups.map((group) => (
            <div key={group.key} className="flex flex-col gap-1.5">
              <div className="text-[10px] font-bold uppercase tracking-wider px-0.5" style={{ color: COLORS.text.tertiary }}>
                {group.label}
              </div>
              <div className="rounded-lg overflow-hidden" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
                {group.buildings.map((b) => {
                  const owned = province.territory.buildings[b.id] ?? 0;
                  const inProg = inProgress[b.id] ?? 0;
                  const pct = pctForAcres(totalLand, owned);
                  const inProgPct = inProg > 0 ? Math.round(pctForAcres(totalLand, inProg)) : null;
                  const targetPct = activeStrategy?.settings?.buildings[b.id] ?? null;
                  const expanded = stepper?.id === b.id;

                  const qty = razeQty[b.id] ?? 0;
                  const draftPct = qty > 0 ? -Math.round(pctForAcres(totalLand, qty)) : null;

                  return (
                    <PercentRow
                      key={b.id}
                      label={b.name}
                      pct={Math.round(pct)}
                      absolute={owned}
                      targetPct={targetPct}
                      inProgressPct={inProgPct}
                      draftPct={draftPct}
                      expanded={expanded}
                      disabled={strategyLoading}
                      onToggle={() => handleToggleRow(b.id)}
                    >
                      {expanded && stepper && (() => {
                        const razeMoreDisabled = stepper.acres <= 0;
                        const razeLessDisabled = stepper.acres >= owned;
                        const queuedNegative = qty > 0;
                        const ownedPctRounded = Math.round(pctForAcres(totalLand, owned));
                        const freedAcres = owned - stepper.acres;
                        const strategyAcres = targetPct !== null ? acresForPct(totalLand, targetPct) : null;
                        const { surplus } = computeRazeSurplus({ targetPct, totalLand, owned });
                        const hasSurplus = surplus > 0;
                        const belowStrategyTarget = strategyAcres !== null && stepper.acres < strategyAcres;

                        return (
                          <>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                aria-label="Raze more"
                                onClick={() => adjustStepper(-onePctAcres)}
                                disabled={razeMoreDisabled}
                                className="rounded-lg font-mono text-sm"
                                style={{ width: 44, height: 44, border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary, opacity: razeMoreDisabled ? 0.5 : 1 }}
                              >
                                −
                              </button>
                              <span
                                className="flex-1 text-center font-mono text-xl font-bold"
                                style={{ color: queuedNegative ? COLORS.negative : COLORS.text.primary }}
                                aria-live="polite"
                              >
                                <span>{stepperDisplayPct}%</span>
                                <small className="block text-[10px] font-sans font-normal" style={{ color: COLORS.text.tertiary }}>
                                  raze down to this % of land
                                </small>
                              </span>
                              <button
                                type="button"
                                aria-label="Raze less"
                                onClick={() => adjustStepper(onePctAcres)}
                                disabled={razeLessDisabled}
                                className="rounded-lg font-mono text-sm"
                                style={{ width: 44, height: 44, border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary, opacity: razeLessDisabled ? 0.5 : 1 }}
                              >
                                +
                              </button>
                            </div>
                            <div className="flex gap-1.5 flex-wrap">
                              <button
                                type="button"
                                onClick={() => adjustStepper(-onePctAcres)}
                                disabled={razeMoreDisabled}
                                className="text-xs font-semibold rounded-full px-2.5"
                                style={{ minHeight: 44, border: `1px solid ${COLORS.border}`, color: COLORS.text.secondary, opacity: razeMoreDisabled ? 0.5 : 1 }}
                              >
                                −1%
                              </button>
                              <button
                                type="button"
                                onClick={() => adjustStepper(-twoPctAcres)}
                                disabled={razeMoreDisabled}
                                className="text-xs font-semibold rounded-full px-2.5"
                                style={{ minHeight: 44, border: `1px solid ${COLORS.border}`, color: COLORS.text.secondary, opacity: razeMoreDisabled ? 0.5 : 1 }}
                              >
                                −2%
                              </button>
                              {stepper.acres < owned && (
                                <button
                                  type="button"
                                  onClick={() => setStepperAcresAbsolute(b.id, owned)}
                                  className="text-xs font-semibold rounded-full px-2.5"
                                  style={{ minHeight: 44, border: `1px solid ${COLORS.border}`, color: COLORS.text.secondary }}
                                >
                                  Keep all
                                </button>
                              )}
                              {hasSurplus && strategyAcres !== null && stepper.acres !== strategyAcres && (
                                <button
                                  type="button"
                                  onClick={() => setStepperAcresAbsolute(b.id, strategyAcres)}
                                  className="text-xs font-semibold rounded-full px-2.5"
                                  style={{
                                    minHeight: 44,
                                    border: '1px solid rgba(255,85,85,0.4)',
                                    backgroundColor: 'rgba(255,85,85,0.08)',
                                    color: COLORS.negative,
                                  }}
                                >
                                  ⚡ Strategy ({targetPct}%)
                                </button>
                              )}
                              {hasSurplus && strategyAcres !== null && stepper.acres === strategyAcres && (
                                <span
                                  className="text-xs font-semibold rounded-full px-2.5 flex items-center"
                                  style={{ minHeight: 44, color: COLORS.text.tertiary }}
                                >
                                  ✓ At strategy ({targetPct}%)
                                </span>
                              )}
                            </div>
                            {belowStrategyTarget && (
                              <div className="text-[11px]" style={{ color: COLORS.warning }}>
                                ⚠ Razing below your {targetPct}% strategy target
                              </div>
                            )}
                            <div
                              className="flex justify-between text-[11px] pt-0.5"
                              style={{ color: COLORS.text.tertiary, borderTop: `1px solid ${COLORS.border}` }}
                            >
                              <span>
                                {ownedPctRounded}% → {stepperDisplayPct}% frees
                              </span>
                              <span className="font-mono font-semibold" style={{ color: COLORS.text.secondary }}>
                                +{formatNumber(freedAcres)} acres
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    </PercentRow>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {bottomBarVisible && (
        <div
          data-testid="raze-bottom-bar"
          className="fixed left-0 right-0 flex flex-col gap-2"
          style={{
            bottom: TAB_BAR_OFFSET,
            backgroundColor: '#0b0e19',
            borderTop: `1px solid ${COLORS.border}`,
            padding: '10px 14px',
            paddingBottom: `calc(10px + env(safe-area-inset-bottom))`,
            zIndex: 30,
          }}
        >
          <div className="flex items-baseline justify-between text-[11px]" style={{ color: COLORS.text.tertiary }}>
            <span>
              Razing{' '}
              <b className="font-mono" style={{ color: COLORS.negative }}>
                -{razePctRounded}% of land
              </b>{' '}
              ({formatNumber(totalToRaze)} acres)
            </span>
            <span className="font-mono font-bold" style={{ color: COLORS.text.primary }}>
              {formatNumber(totalRazeCost)} gc
            </span>
          </div>
          {totalRazeCost > availableGold && (
            <div className="text-[11px]" style={{ color: COLORS.negative }}>
              Insufficient gold: need {formatNumber(totalRazeCost)} gc, have {formatNumber(availableGold)} gc
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleClear}
              aria-disabled={totalToRaze === 0}
              className="flex-1 text-center py-2.5 rounded-lg text-sm font-bold"
              style={{ border: `1px solid ${COLORS.border}`, color: COLORS.text.secondary, minHeight: 44, opacity: totalToRaze === 0 ? 0.5 : 1 }}
            >
              Clear
            </button>
            <button
              onClick={() => {
                if (isSubmitting || totalToRaze === 0) return;
                handleSubmitClick();
              }}
              aria-disabled={isSubmitting || totalToRaze === 0}
              className="flex-1 text-center py-2.5 rounded-lg text-sm font-bold"
              style={{
                backgroundColor: '#4a1414',
                border: `1px solid ${COLORS.negative}`,
                color: '#ffdcdc',
                minHeight: 44,
                opacity: isSubmitting || totalToRaze === 0 ? 0.5 : 1,
              }}
            >
              {isSubmitting ? 'Razing…' : 'Raze Buildings'}
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleSubmit}
        onCancel={() => setShowConfirmModal(false)}
        title="Confirm Raze"
        confirmText="Confirm Raze"
      >
        <div>
          <p className="mb-3">
            Are you sure you want to raze {totalToRaze} acres for {formatNumber(totalRazeCost)} gc?
          </p>
          <ul className="list-disc list-inside mb-3 space-y-1 text-sm">
            {razeBuildingList.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </div>
      </ConfirmModal>
    </div>
  );
}
